import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, Network, Handshake, Lightbulb, Truck, ShieldCheck, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { heroBanners, brands, whyTrustar, companyInfo } from '../data/mockData';

const Home = () => {
  const [currentBanner, setCurrentBanner] = useState(0);
  
  // Filter only visible banners
  const visibleBanners = heroBanners.filter(banner => banner.visible);

  // Auto-rotate banners every 6 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % visibleBanners.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [visibleBanners.length]);

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % visibleBanners.length);
  };

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + visibleBanners.length) % visibleBanners.length);
  };

  const currentSlide = visibleBanners[currentBanner];

  const iconMap = {
    Network: Network,
    Handshake: Handshake,
    Lightbulb: Lightbulb,
    Truck: Truck,
    ShieldCheck: ShieldCheck,
    Heart: Heart
  };

  return (
    <div className="min-h-screen">
      <SEO path="/" />
      {/* Premium Hero Banner - Rotating with sophisticated backgrounds */}
      <section className="relative h-[650px] overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${currentSlide.gradient} transition-all duration-1000`}>
          {/* Enhanced Premium Abstract Pattern Overlay */}
          <div className="absolute inset-0 opacity-25">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.25) 0%, transparent 50%),
                               radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 50%),
                               radial-gradient(circle at 40% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
                               radial-gradient(circle at 60% 90%, rgba(255,255,255,0.1) 0%, transparent 40%)`
            }}></div>
          </div>
          
          {/* Elegant geometric pattern */}
          <div className="absolute inset-0 opacity-15" style={{
            backgroundImage: `
              linear-gradient(30deg, rgba(255,255,255,0.12) 12%, transparent 12.5%, transparent 87%, rgba(255,255,255,0.12) 87.5%),
              linear-gradient(150deg, rgba(255,255,255,0.12) 12%, transparent 12.5%, transparent 87%, rgba(255,255,255,0.12) 87.5%),
              linear-gradient(30deg, rgba(255,255,255,0.08) 12%, transparent 12.5%, transparent 87%, rgba(255,255,255,0.08) 87.5%),
              linear-gradient(150deg, rgba(255,255,255,0.08) 12%, transparent 12.5%, transparent 87%, rgba(255,255,255,0.08) 87.5%)
            `,
            backgroundSize: '80px 140px',
            backgroundPosition: '0 0, 0 0, 40px 70px, 40px 70px'
          }}></div>

          {/* Floating orbs effect */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
          </div>

          <div className="absolute inset-0 bg-black/5"></div>
          
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div className="text-white max-w-4xl">
              <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight drop-shadow-lg">
                {currentSlide.title}
              </h1>
              <p className="text-xl lg:text-2xl mb-10 text-white/95 font-light max-w-3xl leading-relaxed drop-shadow-md">
                {currentSlide.subtitle}
              </p>
              <Link to={currentSlide.ctaLink}>
                <Button size="lg" variant="secondary" className="text-lg px-10 py-6 shadow-xl hover:shadow-2xl">
                  {currentSlide.cta} <ArrowRight className="ml-2" size={22} />
                </Button>
              </Link>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevBanner}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/15 hover:bg-white/25 backdrop-blur-md rounded-full flex items-center justify-center transition-all shadow-lg"
          >
            <ChevronLeft className="text-white" size={28} />
          </button>
          <button
            onClick={nextBanner}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/15 hover:bg-white/25 backdrop-blur-md rounded-full flex items-center justify-center transition-all shadow-lg"
          >
            <ChevronRight className="text-white" size={28} />
          </button>

          {/* Indicators */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex space-x-3">
            {visibleBanners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentBanner ? 'bg-white w-12' : 'bg-white/50 w-8'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Tagline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">{companyInfo.tagline}</h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {companyInfo.heroDescription}
          </p>
        </div>
      </section>

      {/* Our Partners */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Our Manufacturing Partners</h2>
            <p className="text-xl text-gray-600">
              Trusted alliances delivering excellence worldwide
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {brands.map((brand) => (
              <Link
                key={brand.id}
                to={`/products/${brand.slug}`}
                className="group"
              >
                <Card className="h-full border-none shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2">
                  <div className={`h-3 bg-gradient-to-r ${brand.gradient}`}></div>
                  <CardContent className="p-10">
                    {/* Brand Logo */}
                    <div className="h-20 mb-6 flex items-center justify-center">
                      {brand.logoUrl ? (
                        <img 
                          src={brand.logoUrl} 
                          alt={brand.name}
                          className="max-h-16 max-w-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-r from-gray-100 to-gray-200 rounded flex items-center justify-center">
                          <span className="text-2xl font-bold text-gray-400">{brand.name}</span>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-sm text-primary font-semibold mb-4">{brand.tagline}</p>
                    <p className="text-gray-600 mb-6 leading-relaxed line-clamp-4">
                      {brand.description}
                    </p>
                    <div className="flex items-center text-primary font-medium group-hover:translate-x-2 transition-transform">
                      Explore Products <ArrowRight className="ml-2" size={20} />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Global Distribution Excellence</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Trustar Industrial Chemicals delivers world-class titanium dioxide and specialty 
                pigments across three dynamic continents, serving diverse industries with precision 
                and reliability.
              </p>
              <div className="space-y-4">
                {companyInfo.regions.map((region, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-3 h-3 bg-primary rounded-full mr-4"></div>
                    <span className="text-lg font-medium text-gray-800">{region}</span>
                  </div>
                ))}
              </div>
              <Link to="/about" className="inline-block mt-8">
                <Button size="lg" variant="outline">
                  Learn More About Us <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-12 text-center">
              <div className="text-6xl font-bold text-primary mb-4">3</div>
              <p className="text-xl font-semibold text-gray-800 mb-2">Continents Served</p>
              <p className="text-gray-600">Middle East • Africa • Latin America</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Trustar - with relevant icons */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Why Choose Trustar</h2>
            <p className="text-xl text-gray-600">
              Excellence in every aspect of distribution
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyTrustar.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <Card key={item.id} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl flex items-center justify-center mb-5">
                      <Icon className="text-primary" size={32} />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Partner With Us?</h2>
          <p className="text-xl mb-8 text-blue-50">
            Discover premium titanium dioxide and specialty pigments for your applications
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" variant="secondary">
                Request A Quote <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/products/tinergy">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                View Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
